import { utils } from '../core/utils.js';
import { eventBus } from '../core/eventBus.js';
import { emailService } from '../utils/email-service.js';

export class FormValidator {
  constructor(formElement) {
    this.form = formElement;
    this.fields = {};
    this.isSubmitting = false;
    this.autoSaveEnabled = true;
    this.autoSaveInterval = null;
    this.hasUnsavedChanges = false;
    
    this.validationRules = {
      name: [
        { test: (value) => value.trim().length >= 2, message: 'Name must be at least 2 characters' },
        { test: (value) => /^[a-zA-ZÀ-ÿ\s'-]+$/.test(value.trim()), message: 'Name contains invalid characters' }
      ],
      email: [
        { test: (value) => value.trim().length > 0, message: 'Email is required' },
        { test: (value) => utils.isValidEmail(value.trim()), message: 'Please enter a valid email' }
      ],
      subject: [
        { test: (value) => value.trim().length >= 3, message: 'Subject must be at least 3 characters' },
        { test: (value) => value.trim().length <= 100, message: 'Subject is too long' }
      ],
      message: [
        { test: (value) => value.trim().length >= 10, message: 'Message must be at least 10 characters' },
        { test: (value) => value.trim().length <= 2000, message: 'Message is too long' }
      ]
    };
    
    this.init();
  }
  
  init() {
    if (!this.form) return;
    
    this.setupFields();
    this.bindEvents();
    this.loadSavedData();
    this.setupAutoSave();
    this.updateSubmitButton();
  }
  
  setupFields() {
    // Setup main fields
    ['name', 'email', 'subject', 'message'].forEach(fieldName => {
      const element = this.form.querySelector(`#${fieldName}`);
      const errorElement = this.form.querySelector(`#${fieldName} + .form-error`);
      
      if (element) {
        this.fields[fieldName] = {
          element,
          errorElement,
          isValid: false,
          touched: false
        };
      }
    });
    
    // Setup optional fields
    this.setupCharCounters();
    this.setupMessageStrength();
  }
  
  bindEvents() {
    // Form submission
    this.form.addEventListener('submit', (e) => this.handleSubmit(e));
    
    // Field validation
    Object.entries(this.fields).forEach(([fieldName, field]) => {
      // Blur validation
      field.element.addEventListener('blur', () => {
        field.touched = true;
        this.validateField(fieldName);
        this.updateSubmitButton();
      });
      
      // Input validation with debounce
      field.element.addEventListener('input', utils.debounce(() => {
        field.touched = true; // Always mark as touched on input
        this.hasUnsavedChanges = true;
        this.validateField(fieldName);
        this.updateSubmitButton();
      }, 300));
      
      // Clear error on focus
      field.element.addEventListener('focus', () => {
        this.clearFieldError(fieldName);
      });
    });
    
    // Consent checkbox
    const consentCheckbox = this.form.querySelector('#consent');
    consentCheckbox?.addEventListener('change', () => this.updateSubmitButton());
  }
  
  validateField(fieldName) {
    const field = this.fields[fieldName];
    if (!field) return true;

    const value = field.element.value;
    const rules = this.validationRules[fieldName];

    let isValid = true;
    if (rules) {
      for (const rule of rules) {
        if (!rule.test(value)) {
          isValid = false;
          if (field.touched) {
            this.showFieldError(fieldName, rule.message);
          }
          break;
        }
      }
    }

    field.isValid = isValid;
    field.element.classList.toggle('error', !isValid && field.touched);
    field.element.classList.toggle('success', isValid && field.touched);

    if (isValid) {
      this.clearFieldError(fieldName);
    }

    return isValid;
  }
  
  showFieldError(fieldName, message) {
    const field = this.fields[fieldName];
    if (!field || !field.touched) return;
    
    if (field.errorElement) {
      field.errorElement.textContent = message;
    }
  }
  
  clearFieldError(fieldName) {
    const field = this.fields[fieldName];
    if (!field) return;
    
    if (field.errorElement) {
      field.errorElement.textContent = '';
    }
  }
  
  validateAllFields() {
    let allValid = true;
    Object.keys(this.fields).forEach(fieldName => {
      this.fields[fieldName].touched = true;
      if (!this.validateField(fieldName)) {
        allValid = false;
      }
    });
    return allValid;
  }
  
  updateSubmitButton() {
    const submitBtn = this.form.querySelector('button[type="submit"]');
    const consentCheckbox = this.form.querySelector('#consent');
    if (!submitBtn) return;

    // Validate all fields silently (without showing errors)
    Object.keys(this.fields).forEach(fieldName => {
      this.validateField(fieldName);
    });

    const allFieldsValid = Object.values(this.fields).every(field => field.isValid);
    const consentGiven = consentCheckbox?.checked;

    submitBtn.disabled = !allFieldsValid || !consentGiven;
  }
  
  setupCharCounters() {
    const fieldsWithCounters = [
      { id: 'subject', max: 100 },
      { id: 'message', max: 2000 }
    ];
    
    fieldsWithCounters.forEach(({ id, max }) => {
      const field = this.form.querySelector(`#${id}`);
      const counter = field?.parentElement.querySelector('.char-counter');
      
      if (field && counter) {
        const updateCounter = () => {
          const current = field.value.length;
          const currentSpan = counter.querySelector('.current');
          const maxSpan = counter.querySelector('.max');
          
          if (currentSpan) currentSpan.textContent = current;
          if (maxSpan) maxSpan.textContent = max;
          
          counter.classList.remove('warning', 'danger');
          if (current > max * 0.9) {
            counter.classList.add('danger');
          } else if (current > max * 0.8) {
            counter.classList.add('warning');
          }
        };
        
        field.addEventListener('input', updateCounter);
        updateCounter();
      }
    });
  }
  
  setupMessageStrength() {
    const messageField = this.form.querySelector('#message');
    const strengthBars = this.form.querySelectorAll('.message-strength-bar');
    
    if (!messageField || !strengthBars.length) return;
    
    const analyzeMessage = (text) => {
      let score = 0;
      const len = text.length;
      const wordCount = text.split(/\s+/).filter(Boolean).length;

      if (len > 10) score = 1;
      if (len > 50 || wordCount > 10) score = 2;
      if (len > 100 && wordCount > 20) score = 3;
      if (len > 150 && /[.!?]/.test(text)) score = 4;

      return score;
    };
    
    const updateStrength = () => {
      const text = messageField.value.trim();
      const strength = analyzeMessage(text);
      
      strengthBars.forEach((bar, index) => {
        bar.classList.remove('active', 'weak', 'medium', 'strong');
        
        if (index < strength) {
          bar.classList.add('active');
          
          if (strength <= 1) bar.classList.add('weak');
          else if (strength <= 2) bar.classList.add('medium');
          else bar.classList.add('strong');
        }
      });
    };
    
    messageField.addEventListener('input', updateStrength);
  }
  
  async handleSubmit(e) {
    e.preventDefault();
    
    if (this.isSubmitting) return;
    
    // Validate all fields
    if (!this.validateAllFields()) {
      this.showNotification('Please fix the errors above', 'error');
      return;
    }
    
    this.isSubmitting = true;
    this.setLoadingState(true);
    
    try {
      const formData = this.getFormData();
      
      // Send form (adapt based on your backend)
      const response = await this.sendForm(formData);
      
      if (response.ok) {
        this.handleSuccess();
      } else {
        throw new Error('Failed to send message');
      }
    } catch (error) {
      this.handleError(error);
    } finally {
      this.isSubmitting = false;
      this.setLoadingState(false);
    }
  }
  
  async sendForm(formData) {
    // Use EmailService with automatic Netlify -> EmailJS fallback
    const formDataObj = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formDataObj.append(key, value);
    });

    // Progress callback to update UI
    const onProgress = (percent) => {
      this.updateProgress(percent);
    };

    const result = await emailService.sendWithFallback(formDataObj, onProgress);

    if (!result.success) {
      throw result.error || new Error('Failed to send message');
    }

    // Log which method was used
    console.log(`Form sent via: ${result.method} (${result.attempts} attempt(s))`);

    return { ok: true, method: result.method };
  }

  /**
   * Update progress bar during submission
   */
  updateProgress(percent) {
    let progressBar = this.form.querySelector('.submit-progress');

    if (!progressBar) {
      // Create progress bar if it doesn't exist
      const submitBtn = this.form.querySelector('button[type="submit"]');
      if (submitBtn) {
        progressBar = document.createElement('div');
        progressBar.className = 'submit-progress';
        progressBar.innerHTML = '<div class="submit-progress-bar"></div>';
        submitBtn.parentElement.insertBefore(progressBar, submitBtn.nextSibling);
      }
    }

    const bar = progressBar?.querySelector('.submit-progress-bar');
    if (bar) {
      bar.style.width = `${percent}%`;
      progressBar.style.display = percent > 0 && percent < 100 ? 'block' : 'none';
    }
  }
  
  getFormData() {
    const data = {};
    
    // Get all form fields
    const formData = new FormData(this.form);
    for (const [key, value] of formData.entries()) {
      data[key] = value;
    }
    
    return data;
  }
  
  setLoadingState(loading) {
    const submitBtn = this.form.querySelector('button[type="submit"]');
    const btnText = submitBtn?.querySelector('.btn-text');
    const btnIcon = submitBtn?.querySelector('.btn-icon i');
    
    if (loading) {
      submitBtn?.classList.add('loading');
      if (btnText) btnText.textContent = 'Sending...';
      if (btnIcon) btnIcon.className = 'fas fa-spinner fa-spin';
    } else {
      submitBtn?.classList.remove('loading');
      if (btnText) btnText.textContent = 'Send Message';
      if (btnIcon) btnIcon.className = 'fas fa-paper-plane';
    }
  }
  
  handleSuccess() {
    // Reset form
    this.form.reset();
    Object.values(this.fields).forEach(field => {
      field.isValid = false;
      field.touched = false;
      field.element.classList.remove('error', 'success');
    });
    
    // Clear saved data
    this.clearSavedData();
    
    // Show success notification
    this.showNotification(
      'Thank you! Your message has been sent successfully.',
      'success'
    );
    
    // Emit event
    eventBus.emit('form:submitted', { form: 'contact' });
  }
  
  handleError(error) {
    this.showNotification(
      'Sorry, there was an error sending your message. Please try again.',
      'error'
    );
    
    console.error('Form submission error:', error);
  }
  
  showNotification(message, type = 'info') {
    // Remove existing notifications
    document.querySelectorAll('.form-notification').forEach(n => n.remove());

    // Announce to screen readers via aria-live region
    const liveRegion = document.getElementById('aria-live-region');
    if (liveRegion) {
      liveRegion.textContent = message;
    }

    // Create notification
    const notification = document.createElement('div');
    notification.className = `form-notification form-notification--${type}`;
    notification.setAttribute('role', 'alert');
    notification.innerHTML = `
      <div class="form-notification__content">
        <i class="fas ${this.getNotificationIcon(type)}" aria-hidden="true"></i>
        <span>${message}</span>
        <button class="form-notification__close" aria-label="Close notification">
          <i class="fas fa-times" aria-hidden="true"></i>
        </button>
      </div>
    `;

    document.body.appendChild(notification);

    // Show animation
    requestAnimationFrame(() => {
      notification.classList.add('show');
    });

    // Close button
    const closeBtn = notification.querySelector('.form-notification__close');
    closeBtn?.addEventListener('click', () => this.hideNotification(notification));

    // Auto-hide after 5 seconds
    setTimeout(() => this.hideNotification(notification), 5000);
  }
  
  hideNotification(notification) {
    notification.classList.remove('show');
    setTimeout(() => notification.remove(), 300);
  }
  
  getNotificationIcon(type) {
    const icons = {
      success: 'fa-check-circle',
      error: 'fa-exclamation-circle',
      warning: 'fa-exclamation-triangle',
      info: 'fa-info-circle'
    };
    return icons[type] || icons.info;
  }
  
  setupAutoSave() {
    if (!this.autoSaveEnabled) return;

    // Load saved data on init
    this.loadSavedData();

    // Save periodically only if there are unsaved changes
    this.autoSaveInterval = setInterval(() => {
      if (this.hasUnsavedChanges) {
        this.saveFormData();
      }
    }, 30000); // Every 30 seconds
  }
  
  saveFormData() {
    const data = this.getFormData();
    data.savedAt = Date.now();
    utils.storage.set('contactFormData', data);
    this.hasUnsavedChanges = false;

    // Show indicator
    this.showAutoSaveIndicator();
  }
  
  loadSavedData() {
    const savedData = utils.storage.get('contactFormData');
    if (!savedData) return;

    // Check if data is less than 24 hours old
    const now = Date.now();
    const dayInMs = 24 * 60 * 60 * 1000;

    if (now - savedData.savedAt > dayInMs) {
      this.clearSavedData();
      return;
    }

    // Restore data
    Object.entries(savedData).forEach(([key, value]) => {
      const field = this.form.querySelector(`[name="${key}"]`);
      if (field && key !== 'savedAt') {
        field.value = value;
      }
    });

    // Validate and update submit button after restoring
    this.updateSubmitButton();

    // Show notification
    this.showNotification('Draft restored from previous session', 'info');
  }
  
  clearSavedData() {
    utils.storage.remove('contactFormData');
  }
  
  showAutoSaveIndicator() {
    const indicator = this.form.querySelector('.auto-save-indicator');
    if (!indicator) return;
    
    indicator.style.display = 'flex';
    indicator.classList.add('show');
    
    setTimeout(() => {
      indicator.classList.remove('show');
      setTimeout(() => {
        indicator.style.display = 'none';
      }, 300);
    }, 2000);
  }
  
  destroy() {
    if (this.autoSaveInterval) {
      clearInterval(this.autoSaveInterval);
    }
  }
}